import { useState, useEffect, useId, createElement } from 'react'
import { useNavigate } from 'react-router-dom'
import { pdf } from '@react-pdf/renderer'
import { ChevronDown, ChevronUp, ArrowLeft, TrendingUp, BarChart3, X, Plus, Download } from 'lucide-react'
import CEDECComparisonReport from './CEDECComparisonReport'
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js'
import { Radar, Line, Bar } from 'react-chartjs-2'
import {
  getColor,
  formatDelta,
  getDeltaColor,
  getDeltaArrow,
  formatComparisonDate,
  assessmentLabel,
  personCompanyLabel,
  legendLabel,
  radarPointLabel,
  prepareRadarData,
  prepareEvolutionData,
  prepareGroupedBarData,
  getScoreColor,
} from '../utils/comparisonHelper'

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Filler,
  Tooltip,
  Legend
)

// ── Accessible visually-hidden class (Tailwind built-in) ────
const srOnly = 'sr-only'

// ── Score pill ──────────────────────────────────────────────
const ScorePill = ({ score, colorIndex, tooltip }) => {
  const c = getColor(colorIndex)
  if (score === null || score === undefined) {
    return <span className="inline-flex px-2 py-0.5 text-xs text-gray-400 bg-gray-100 rounded" title={tooltip}>N/A</span>
  }
  return (
    <span
      className="inline-flex px-2.5 py-1 text-sm font-bold rounded-lg cursor-default"
      style={{ backgroundColor: `rgba(${c.rgb}, 0.15)`, color: c.hex }}
      title={tooltip}
    >
      {typeof score === 'number' ? score.toFixed(1) : score}/10
    </span>
  )
}

// ── Delta badge ─────────────────────────────────────────────
const DeltaBadge = ({ delta }) => {
  if (!delta) return null
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${getDeltaColor(delta.delta)}`}>
      {getDeltaArrow(delta.delta)} {formatDelta(delta.delta)}
    </span>
  )
}

const ComparisonView = ({ assessmentIds, onClose, onRemoveAssessment, onRequestAdd }) => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [comparisonData, setComparisonData] = useState(null)
  const [expandedThemes, setExpandedThemes] = useState({})
  const [isExporting, setIsExporting] = useState(false)
  const [exportError, setExportError] = useState(null)
  const uid = useId()

  useEffect(() => {
    fetchComparison()
  }, [assessmentIds])

  const fetchComparison = async () => {
    try {
      setLoading(true)
      setError(null)
      const token = localStorage.getItem('adminToken')
      const response = await fetch('/api/admin/assessments/compare', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ assessmentIds })
      })
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Erreur lors de la comparaison')
      }
      const data = await response.json()
      setComparisonData(data.data)
    } catch (err) {
      console.error('Error fetching comparison:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const toggleTheme = (themeId) => {
    setExpandedThemes(prev => ({ ...prev, [themeId]: !prev[themeId] }))
  }

  const handleKeyToggle = (e, themeId) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      toggleTheme(themeId)
    }
  }

  const goBack = onClose || (() => navigate('/admin/assessments'))

  const handleExportPDF = async () => {
    if (!comparisonData) return
    try {
      setIsExporting(true)
      setExportError(null)

      const pdfComponent = createElement(CEDECComparisonReport, {
        assessments: comparisonData.assessments,
        comparison: comparisonData.comparison,
      })

      const pdfBlob = await pdf(pdfComponent).toBlob()
      const url = window.URL.createObjectURL(pdfBlob)
      const a = document.createElement('a')
      a.href = url
      const companies = [...new Set(comparisonData.assessments.map(x => x.clientInfo.company))]
      const slug = companies.join('-').replace(/[^a-zA-Z0-9-]/g, '')
      const now = new Date()
      a.download = `CEDEC-Comparaison-${slug}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}.pdf`
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      setTimeout(() => {
        if (document.body.contains(a)) document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      }, 1000)
    } catch (err) {
      console.error('PDF export error:', err)
      setExportError('Échec de la génération du PDF.')
    } finally {
      setIsExporting(false)
    }
  }

  // ── Loading / Error states ──────────────────────────────
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Chargement de la comparaison...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="bg-red-50 border border-red-200 text-red-600 px-6 py-4 rounded-lg" role="alert">
          <h3 className="font-medium">Erreur</h3>
          <p>{error}</p>
        </div>
        <button onClick={goBack} className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>
      </div>
    )
  }

  if (!comparisonData) return null

  const { assessments, comparison } = comparisonData
  const n = assessments.length
  const radarData = prepareRadarData(assessments, comparison)
  const evolutionData = prepareEvolutionData(assessments, comparison)
  const barData = prepareGroupedBarData(assessments, comparison)

  // ── Chart options ─────────────────────────────────────────
  // Legend text is clipped by the canvas edge, so trim each entry to what the
  // current chart width can display. Full text stays in the tooltips.
  const compactLegend = {
    boxWidth: 12,
    padding: 10,
    font: { size: 11 },
    generateLabels: (chart) => {
      const items = ChartJS.defaults.plugins.legend.labels.generateLabels(chart)
      return items.map(item => ({ ...item, text: legendLabel(item.text, chart.width) }))
    },
  }

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'bottom', labels: compactLegend },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.r}/10`
        }
      }
    },
    scales: {
      r: {
        angleLines: { display: true, color: 'rgba(0,0,0,0.1)' },
        suggestedMin: 0,
        suggestedMax: 10,
        ticks: { stepSize: 2, color: '#666', backdropColor: 'transparent', font: { size: 10 } },
        pointLabels: {
          color: '#333',
          font: (ctx) => ({ size: ctx.chart.width < 420 ? 9 : 11, weight: 'bold' }),
          // Theme titles are stored in full; trim them to the room the chart has.
          callback: function (label) {
            return radarPointLabel(label, this.chart.width)
          },
        },
        grid: { color: 'rgba(0,0,0,0.1)' },
      }
    }
  }

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'bottom', labels: compactLegend },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(1)}/10`
        }
      }
    },
    scales: {
      y: { min: 0, max: 10, ticks: { stepSize: 1 }, title: { display: true, text: 'Score' } },
      x: { title: { display: true, text: 'Date d\'évaluation' } }
    }
  }

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'bottom', labels: compactLegend },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(1)}/10`
        }
      }
    },
    scales: {
      y: { min: 0, max: 10, ticks: { stepSize: 2 }, title: { display: true, text: 'Score' } },
      x: { ticks: { maxRotation: 45, minRotation: 0, font: { size: 10 } } }
    }
  }

  return (
    <div className="space-y-6">

      {/* ── Header ──────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button
          onClick={goBack}
          className="text-blue-600 hover:text-blue-800 flex items-center gap-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
        >
          <ArrowLeft className="w-5 h-5" /> Retour
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          Comparaison de {n} évaluations
        </h1>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportPDF}
            disabled={isExporting}
            className="text-green-600 hover:text-green-800 flex items-center text-sm font-medium border border-green-200 px-3 py-1 rounded-lg hover:bg-green-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-1.5" />
                Génération...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-1.5" />
                Exporter PDF
              </>
            )}
          </button>
        </div>
      </div>
      {exportError && (
        <p className="text-xs text-red-600">{exportError}</p>
      )}

      {/* ── Version warnings ────────────────────────────── */}
      {comparison.metadata.warnings.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4" role="alert">
          <div className="flex">
            <svg className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Attention</h3>
              {comparison.metadata.warnings.map((w, i) => (
                <p key={i} className="text-sm text-yellow-700 mt-1">{w.message}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Section 1 — Assessment Cards ────────────────── */}
      <div
        className={`grid gap-4 ${
          n === 2 ? 'grid-cols-1 md:grid-cols-2' :
          n === 3 ? 'grid-cols-1 md:grid-cols-3' :
          n === 4 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' :
                    'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5'
        }`}
      >
        {assessments.map((a, i) => {
          const c = getColor(i)
          return (
            <div
              key={a.id}
              className="bg-white rounded-lg shadow border-t-4 p-4 flex flex-col relative group"
              style={{ borderTopColor: c.hex }}
            >
              {onRemoveAssessment && (
                <button
                  onClick={() => onRemoveAssessment(a.id)}
                  className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center
                    text-gray-400 bg-gray-100 opacity-0 group-hover:opacity-100
                    hover:bg-red-100 hover:text-red-600
                    focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400
                    transition-all"
                  aria-label={`Retirer ${a.clientInfo.firstName} ${a.clientInfo.lastName} de la comparaison`}
                  title="Retirer de la comparaison"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
              <div className="flex items-center gap-2 mb-2">
                <span
                  className="inline-flex items-center justify-center w-7 h-7 rounded-full text-white text-sm font-bold flex-shrink-0"
                  style={{ backgroundColor: c.hex }}
                >
                  {i + 1}
                </span>
                <h2
                  className="text-base font-bold text-gray-900 truncate min-w-0"
                  title={`${a.clientInfo.firstName} ${a.clientInfo.lastName}`}
                >
                  {a.clientInfo.firstName} {a.clientInfo.lastName}
                </h2>
              </div>
              <p className="text-sm text-gray-600 truncate" title={a.clientInfo.company}>{a.clientInfo.company}</p>
              <p className="text-xs text-gray-500 mt-1">{formatComparisonDate(a.clientInfo.assessmentDate)}</p>
              <div className="mt-auto pt-3 flex items-center justify-between">
                <span className={`inline-flex px-3 py-1 text-lg font-bold rounded-lg ${getScoreColor(a.averageScore)}`}>
                  {a.averageScore.toFixed(1)}/10
                </span>
                <span className="text-xs text-gray-400">
                  {a.questionnaireVersion ? `v${a.questionnaireVersion}` : 'legacy'}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {a.completedQuestions}/{a.totalQuestions} questions
              </p>
            </div>
          )
        })}

        {onRequestAdd && (
          <button
            onClick={onRequestAdd}
            className="bg-white rounded-lg shadow border-2 border-dashed border-gray-300 p-4 flex flex-col items-center justify-center gap-2
              text-gray-400 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50/50
              focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500
              transition-colors cursor-pointer min-h-[140px]"
            aria-label="Ajouter une évaluation à la comparaison"
          >
            <span className="w-10 h-10 rounded-full border-2 border-current flex items-center justify-center">
              <Plus className="w-5 h-5" />
            </span>
            <span className="text-sm font-medium">Ajouter</span>
          </button>
        )}
      </div>

      {/* ── Section 2 — Overall Score Summary ───────────── */}
      <div className="bg-white rounded-lg shadow p-6" role="region" aria-label="Score global comparé">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Score global</h3>

        {/* Horizontal bar visualization */}
        <div className="space-y-3">
          {assessments.map((a, i) => {
            const c = getColor(i)
            const score = comparison.overall.scores[i]
            const pct = (score / 10) * 100
            return (
              <div key={a.id} className="flex items-center gap-3">
                <span
                  className="w-7 h-7 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: c.hex }}
                >
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    {/* min-w-0 lets the label shrink instead of pushing the row wider.
                        Narrow screens drop the date to keep more of the name visible. */}
                    <span className="text-sm font-medium text-gray-700 truncate min-w-0" title={assessmentLabel(a)}>
                      <span className="sm:hidden">{personCompanyLabel(a)}</span>
                      <span className="hidden sm:inline">{assessmentLabel(a)}</span>
                    </span>
                    <span className="text-sm font-bold flex-shrink-0" style={{ color: c.hex }}>
                      {score.toFixed(1)}/10
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${pct}%`, backgroundColor: c.hex }}
                      role="progressbar"
                      aria-valuenow={score}
                      aria-valuemin={0}
                      aria-valuemax={10}
                      aria-label={`Score ${score.toFixed(1)} sur 10`}
                    />
                  </div>
                </div>
                {i > 0 && comparison.overall.deltas[i - 1] && (
                  <DeltaBadge delta={comparison.overall.deltas[i - 1]} />
                )}
              </div>
            )
          })}
        </div>

        {/* Stats summary */}
        <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-6 text-sm text-gray-600">
          <div>Min : <span className="font-bold text-gray-900">{comparison.overall.stats.min?.toFixed(1)}</span></div>
          <div>Max : <span className="font-bold text-gray-900">{comparison.overall.stats.max?.toFixed(1)}</span></div>
          <div>Moyenne : <span className="font-bold text-gray-900">{comparison.overall.stats.avg?.toFixed(1)}</span></div>
          <div>Écart : <span className="font-bold text-gray-900">{comparison.overall.stats.min !== null ? (comparison.overall.stats.max - comparison.overall.stats.min).toFixed(1) : '--'}</span></div>
        </div>

        {/* Screen reader table - wrapped so its width never widens the page */}
        <div className={srOnly}>
        <table>
          <caption>Scores globaux des évaluations</caption>
          <thead><tr><th>Évaluation</th><th>Score</th></tr></thead>
          <tbody>
            {assessments.map((a, i) => (
              <tr key={a.id}><td>{assessmentLabel(a)}</td><td>{comparison.overall.scores[i].toFixed(1)}/10</td></tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* ── Section 3 — Radar Chart ────────────────────── */}
      {radarData && (
        <div className="bg-white rounded-lg shadow p-6" role="region" aria-label="Comparaison radar par thème">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Comparaison par thème</h3>
          {/* The radar is square, so a tall box only adds blank space on narrow screens */}
          <div className="h-80 sm:h-[28rem]">
            <Radar data={radarData} options={radarOptions} />
          </div>
          <div className={srOnly}>
          <table>
            <caption>Scores par thème pour chaque évaluation</caption>
            <thead>
              <tr>
                <th>Thème</th>
                {assessments.map((a, i) => <th key={i}>{assessmentLabel(a)}</th>)}
              </tr>
            </thead>
            <tbody>
              {comparison.themes.filter(t => t.matched).map(t => (
                <tr key={t.id}>
                  <td>{t.title}</td>
                  {t.scores.map((s, i) => <td key={i}>{s !== null ? `${s.toFixed(1)}/10` : 'N/A'}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* ── Section 4 — Evolution Line Chart ───────────── */}
      {evolutionData && (
        <div className="bg-white rounded-lg shadow p-6" role="region" aria-label="Évolution des scores dans le temps">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-gray-700" aria-hidden="true" />
            <h3 className="text-lg font-semibold text-gray-900">Évolution dans le temps</h3>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Score global affiché par défaut. Cliquez sur les thèmes dans la légende pour les afficher.
          </p>
          <div className="h-80">
            <Line data={evolutionData} options={lineOptions} />
          </div>
        </div>
      )}

      {/* ── Section 5 — Grouped Bar Chart ──────────────── */}
      {barData && (
        <div className="bg-white rounded-lg shadow p-6" role="region" aria-label="Comparaison par thème en barres">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-gray-700" aria-hidden="true" />
            <h3 className="text-lg font-semibold text-gray-900">Performance par thème</h3>
          </div>
          <div className="h-96">
            <Bar data={barData} options={barOptions} />
          </div>
        </div>
      )}

      {/* ── Section 6 — Theme Detail Accordion ─────────── */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Détail par thème</h3>
          <p className="text-sm text-gray-600 mt-1">
            {comparison.stats.matchedThemes} thèmes correspondants
            {comparison.stats.unmatchedThemes > 0 && `, ${comparison.stats.unmatchedThemes} non correspondants`}
          </p>
          {/* Color legend */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3">
            {assessments.map((a, i) => {
              const c = getColor(i)
              return (
                <div key={a.id} className="flex items-center gap-1.5 min-w-0 max-w-full">
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: c.hex }} aria-hidden="true" />
                  <span className="text-xs text-gray-700 truncate" title={assessmentLabel(a)}>{personCompanyLabel(a)}</span>
                </div>
              )
            })}
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {comparison.themes.map((theme) => {
            const isExpanded = expandedThemes[theme.id]
            const panelId = `${uid}-panel-${theme.id}`
            const headerId = `${uid}-header-${theme.id}`

            return (
              <div key={theme.id} className={!theme.matched ? 'opacity-60' : ''}>
                {/* Accordion header */}
                <div
                  id={headerId}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  aria-controls={panelId}
                  onClick={() => toggleTheme(theme.id)}
                  onKeyDown={(e) => handleKeyToggle(e, theme.id)}
                  className={`px-6 py-4 flex items-center gap-4 cursor-pointer transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 ${isExpanded ? 'bg-blue-50' : ''}`}
                >
                  <span className="text-2xl flex-shrink-0" aria-hidden="true">{theme.icon}</span>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-gray-900">{theme.title}</h4>
                    {!theme.matched && (
                      <p className="text-xs text-gray-500">Thème absent dans certaines évaluations</p>
                    )}
                    {/* Mini score pills row */}
                    <div className="flex flex-wrap items-center gap-2 mt-1.5">
                      {theme.scores.map((score, i) => (
                        <ScorePill key={i} score={score} colorIndex={i} tooltip={assessmentLabel(assessments[i])} />
                      ))}
                    </div>
                  </div>
                  {/* Min/max spread for matched themes */}
                  {theme.matched && theme.stats.min !== null && (
                    <div className="hidden sm:flex flex-col items-end text-xs text-gray-500 flex-shrink-0">
                      <span>Écart: {(theme.stats.max - theme.stats.min).toFixed(1)}</span>
                      <span>Moy: {theme.stats.avg.toFixed(1)}</span>
                    </div>
                  )}
                  <div className="text-gray-400 flex-shrink-0" aria-hidden="true">
                    {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </div>
                </div>

                {/* Accordion panel — question-by-question detail */}
                {isExpanded && (
                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={headerId}
                    className="px-6 py-5 bg-gray-50 border-t border-gray-200"
                  >
                    <h5 className="text-sm font-semibold text-gray-900 mb-4">Questions détaillées</h5>
                    <QuestionList
                      theme={theme}
                      assessments={assessments}
                      comparison={comparison}
                    />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ── Question list sub-component ───────────────────────────
const QuestionList = ({ theme, assessments, comparison }) => {
  const allScores = assessments.map(a => a.scores || {})
  // Theme ids can differ between questionnaire revisions, so each assessment
  // is looked up with the id the comparison resolved for it
  const allThemes = assessments.map((a, i) => {
    const themeId = theme.themeIds?.[i] ?? theme.id
    return themeId ? a.themes?.find(t => t.id === themeId) : null
  })

  const allQuestionIds = new Set()
  allThemes.forEach(t => {
    if (t?.questions) t.questions.forEach(q => allQuestionIds.add(q.id))
  })

  if (allQuestionIds.size === 0) {
    return <p className="text-sm text-gray-500 text-center py-4">Aucune question disponible pour ce thème</p>
  }

  const questionMeta = new Map()
  allThemes.forEach(t => {
    if (t?.questions) t.questions.forEach(q => { if (!questionMeta.has(q.id)) questionMeta.set(q.id, q) })
  })

  return (
    <div className="space-y-3">
      {Array.from(allQuestionIds).map(qId => {
        const meta = questionMeta.get(qId)
        const scores = allScores.map(s => s[qId] ?? null)
        const validScores = scores.filter(s => s !== null)
        const borderColor = validScores.length > 0
          ? (Math.max(...validScores) >= 7 ? '#10b981' : Math.max(...validScores) >= 5 ? '#f59e0b' : '#ef4444')
          : '#9ca3af'

        return (
          <div
            key={qId}
            className="bg-white rounded-lg p-4 border-l-4 shadow-sm"
            style={{ borderLeftColor: borderColor }}
          >
            <h6 className="text-sm font-semibold text-gray-900 mb-3">{meta?.title || qId}</h6>
            <div className="flex flex-wrap items-center gap-2">
              {scores.map((score, i) => {
                const c = getColor(i)
                const delta = i > 0 && scores[i - 1] !== null && score !== null
                  ? score - scores[i - 1]
                  : null
                return (
                  <div key={i} className="flex items-center gap-1.5">
                    {i > 0 && delta !== null && (
                      <span className={`text-xs font-semibold ${getDeltaColor(delta)} px-1 rounded`}>
                        {getDeltaArrow(delta)}
                      </span>
                    )}
                    <span
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-sm font-bold cursor-default"
                      style={{ backgroundColor: `rgba(${c.rgb}, 0.12)`, color: c.hex }}
                      title={assessmentLabel(assessments[i])}
                    >
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: c.hex }}
                        aria-hidden="true"
                      />
                      {score !== null ? `${score}/10` : 'N/A'}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default ComparisonView
